// Agent location search bar — powered by the same OpenDB location dataset
// used by the landing page. User types a suburb/city/town, selects a match,
// and we resolve it to an H3 cell to query nearby agents via the API.

import { useEffect, useRef, useState } from 'react';
import { useCombobox } from 'downshift';
import { IonIcon } from '@ionic/react';
import { searchOutline, closeOutline, locationOutline } from 'ionicons/icons';
import { searchPlaces, PLACE_LABEL, type NamedPlace } from '@/lib/geo/places';
import { resolveSearcherOriginFromName } from '@/lib/api/geolocation';
import { api } from '@/lib/api/client';

interface AgentSearchBarProps {
  onResult: (agents: any[], placeName: string) => void;
  onClear: () => void;
}

const rowBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  width: '100%',
  border: 'none',
  color: 'var(--bp-text)',
  padding: '10px 14px',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: '0.85rem',
  borderRadius: '8px',
};

export function AgentSearchBar({ onResult, onClear }: AgentSearchBarProps) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<NamedPlace[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasQuery = query.trim().length > 0;

  const {
    isOpen,
    highlightedIndex,
    getMenuProps,
    getInputProps,
    getItemProps,
    setInputValue,
  } = useCombobox<NamedPlace>({
    items: hits,
    itemToString: (item) => (item ? item.name : ''),
    onInputValueChange: ({ inputValue }) => {
      setQuery(inputValue ?? '');
      setSelected(null);
    },
    onSelectedItemChange: async ({ selectedItem }) => {
      if (!selectedItem) return;
      setSelected(selectedItem.name);
      setInputValue(selectedItem.name);
      setLoading(true);
      try {
        const origin = await resolveSearcherOriginFromName(selectedItem.name);
        if (origin?.h3Origin) {
          const res = await api.get<{ success: boolean; data: any[] }>('/workseeking/agents/nearby', {
            params: { h3Index: origin.h3Origin },
          });
          onResult(res.data ?? [], selectedItem.name);
        }
      } catch {
        onResult([], selectedItem.name);
      } finally {
        setLoading(false);
      }
    },
  });

  // Live autosearch against the in-browser dataset (debounced).
  useEffect(() => {
    if (!hasQuery) {
      setHits([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      searchPlaces(query, 12)
        .then((res) => {
          setHits(res);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }, 120);
    return () => clearTimeout(t);
  }, [query, hasQuery]);

  const handleClear = () => {
    setInputValue('');
    setQuery('');
    setSelected(null);
    setHits([]);
    onClear();
  };

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '480px', margin: '0 auto 2rem' }}>
      {/* Search input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          background: 'var(--bp-surface)',
          border: '1.5px solid var(--bp-border)',
          borderRadius: 'var(--bp-radius-lg)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
      >
        <IonIcon
          icon={selected ? locationOutline : searchOutline}
          style={{ fontSize: '1.1rem', color: 'var(--bp-text-muted)', flexShrink: 0 }}
        />
        <input
          {...getInputProps({ ref: inputRef })}
          placeholder="Search by suburb, city, or town…"
          aria-label="Search for agents near a location"
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--bp-text)',
            fontSize: '0.9rem',
          }}
        />
        {(hasQuery || selected) && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            style={{ background: 'transparent', border: 'none', color: 'var(--bp-text-muted)', cursor: 'pointer', display: 'flex' }}
          >
            <IonIcon icon={closeOutline} style={{ fontSize: '1rem' }} />
          </button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {isOpen && hits.length > 0 && !selected && (
        <ul
          {...getMenuProps()}
          style={{
            listStyle: 'none',
            margin: '4px 0 0',
            padding: 0,
            background: 'var(--bp-surface)',
            border: '1px solid var(--bp-border)',
            borderRadius: 'var(--bp-radius-md)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            overflow: 'hidden',
            maxHeight: '280px',
            overflowY: 'auto',
            zIndex: 100,
            position: 'absolute',
            left: 0,
            right: 0,
          }}
        >
          {hits.map((item, index) => {
            const isHighlighted = highlightedIndex === index;
            return (
              <li
                key={`${item.name}:${item.latitude}:${item.longitude}`}
                {...getItemProps({ item, index })}
                style={{
                  ...rowBase,
                  background: isHighlighted ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IonIcon icon={locationOutline} style={{ fontSize: '0.9rem', color: 'var(--bp-blue-400)' }} />
                  {item.name}
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--bp-text-muted)' }}>
                  {PLACE_LABEL[item.placeType] ?? item.placeType}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Loading indicator */}
      {isOpen && loading && hits.length === 0 && (
        <div
          style={{
            padding: '14px',
            fontSize: '0.8rem',
            color: 'var(--bp-text-muted)',
            background: 'var(--bp-surface)',
            border: '1px solid var(--bp-border)',
            borderRadius: 'var(--bp-radius-md)',
            marginTop: '4px',
          }}
        >
          Searching…
        </div>
      )}

      {/* No results */}
      {isOpen && !loading && hasQuery && hits.length === 0 && (
        <div
          style={{
            padding: '14px',
            fontSize: '0.8rem',
            color: 'var(--bp-text-muted)',
            background: 'var(--bp-surface)',
            border: '1px solid var(--bp-border)',
            borderRadius: 'var(--bp-radius-md)',
            marginTop: '4px',
          }}
        >
          No matching places. Try a larger area.
        </div>
      )}
    </div>
  );
}
